import React, { useEffect, useRef } from 'react';
import { Menu, TitleNameSlug, MenuIndustry, MenuTireIndustry } from '../../@types/Menu';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import Link from 'next/link';
import { cn } from '@/lib/utils';

interface ComplexNavButtonProps {
  menu: Menu;
  position?: 'left' | 'right';
  scrollOffset?: number;
}

export default function ComplexNavButton({ menu, position = 'left', scrollOffset = 0 }: ComplexNavButtonProps) {
  const [selectedIndustryIndex, setSelectedIndustryIndex] = React.useState(0);
  const [buttonPosition, setButtonPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const [windowWidth, setWindowWidth] = React.useState(0);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (popoverButtonRef.current) {
        const rect = popoverButtonRef.current.getBoundingClientRect();
        setButtonPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
        setWindowWidth(window.innerWidth);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, []);

  const showPopover = () => {
    if (popoverRef.current) {
      popoverRef.current.style.display = 'block';
    }
  };

  const hidePopover = () => {
    if (popoverRef.current) {
      popoverRef.current.style.display = 'none';
    }
  };

  const chunkArray = (array: any[] = [], chunkSize: number = 7) => {
    if (!array) return [];
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const getLeftMenuItems = (): TitleNameSlug[] => {
    return Object.entries(menu)
      .filter(([key]) => key === 'industries' || key === 'tires_industries')
      .flatMap(([_, value]) => value as TitleNameSlug[]);
  };

  const getRightMenuItems = (): TitleNameSlug[] => {
    const selectedItem = getLeftMenuItems()[selectedIndustryIndex];
    if (!selectedItem) return [];

    const isIndustry = (item: any): item is MenuIndustry => 'families' in item;
    const isTireIndustry = (item: any): item is MenuTireIndustry => 'tires' in item;

    if (isIndustry(selectedItem)) {
      let newSlug = `productos-nuevos/${selectedItem.slug}`
      const newSlectedItem = selectedItem.families.map((item) => ({
        ...item,
        slug: `${newSlug}/${item.slug}`
      }))
      return newSlectedItem
    }

    if (isTireIndustry(selectedItem)) {
      let newSlug = `${menu.slug}/${selectedItem.slug}`;
      return selectedItem.tires.map((item) => ({
        ...item,
        slug: `${newSlug}/${item.slug}`,
      }));
    }

    return [];
  };

  return (
    <Popover className="relative isolate z-50">
      {({ open, close }) => (
        <>
          <PopoverButton
            ref={popoverButtonRef}
            onClick={() => {
              hidePopover()
              window.location.href = `/${menu.slug}`
            }}
            className="group inline-flex items-center gap-x-1 leading-6 text-gray-900 py-4 whitespace-nowrap"
            onMouseEnter={showPopover}
          >
            <Link
              href={`/${menu.slug}`}
              className="uppercase text-ellipsis overflow-hidden"
              onClick={hidePopover}
            >
              {menu.name}
              <span className=" hidden absolute bottom-0 left-0 right-0 h-[5px] bg-transparent group-hover:bg-matco transition-colors duration-200 z-50"></span>
            </Link>
          </PopoverButton>
          <div
            ref={popoverRef}
            className="invisible group-hover:visible fixed left-0 right-0 top-[125px] transition-opacity duration-200"
          >
            <div className="w-full h-[5px] bg-gray02"></div>
            <div className="absolute -top-4 left-0 right-0 h-4 bg-transparent"></div>
            <PopoverPanel
              static
              className="transition-all duration-200 ease-out transform group-hover:translate-y-0 group-hover:opacity-100 -translate-y-1 opacity-0"
            >
              <div className="w-full">
                <div className="mx-auto max-w-[1920px] grid grid-cols-1 lg:grid-cols-4">
                  {/* Columna izquierda */}
                  <div className="col-span-1 bg-white min-h-[300px] pt-4">
                    <div className="flow-root h-full">
                      <div className="h-full">
                        {getLeftMenuItems().map((item, index) => (
                          <div key={item.title} className={cn("flex flex-col gap-1", index === selectedIndustryIndex ? 'bg-[#F2F2F2] font-semibold' : 'hover:bg-gray-50')}>
                            <Link
                              href={`/${menu.slug}/${item.slug}`}
                              className={cn("flex gap-x-4 py-2 pl-20 text-sm leading-6 text-gray-900 uppercase")}
                              onMouseEnter={() => setSelectedIndustryIndex(index)}
                              onClick={hidePopover}
                            >
                              {item.title}
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Columnas derecha */}
                  <div className="col-span-3 bg-[#F2F2F2] bg-opacity-95 min-h-[300px] pt-6 pb-8 px-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 h-full">
                      {chunkArray(getRightMenuItems(), 10).map((chunk, columnIndex) => (
                        <div key={columnIndex} className="flex flex-col space-y-2">
                          {chunk.map((item) => (
                            <Link
                              key={item.title}
                              href={`/${item.slug}`}
                              className="text-sm text-gray-900 hover:text-yellow-500 uppercase px-4"
                              onClick={hidePopover}
                            >
                              {item.title}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </PopoverPanel>
          </div>
        </>
      )}
    </Popover>
  );
}
